import moment from 'moment';

import { REPORT_TYPE_OPTIONS_FUNNEL_POSITION } from '../../constants/options';
import { DEFAULT_DERIVED_TIERS } from '../../constants/tiers';
import { CHANNEL_MAPPING, PLATFORM_MAPPING, AD_TYPE_MAPPING, AUDIENCE_TARGETING_MAPPING } from '../filters/DerivedTiersFilter/constants';

export const METRICS = ['Conversions', 'Revenue', 'Spend', 'Cpa', 'Roas'];

export const capitalizeText = text => {
  if (!text) return 'All';
  return text
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const compareObjects = (obj1, obj2, skipKey) => {
  const keys1 = Object.keys(obj1).filter(x => x !== skipKey);
  const keys2 = Object.keys(obj2).filter(x => x !== skipKey);

  if (keys1.length !== keys2.length) {
    return false;
  }

  return keys1.every(key => {
    if (!keys2.includes(key)) {
      return false;
    }

    return obj1[key] === obj2[key];
  });
};

export const existingFilterMatchFound = (postParams, paramsArray) => {
  for (let i = 0; i < paramsArray.length; i++) {
    const currentParams = paramsArray[i];
    if (compareObjects(currentParams.hash, postParams.hash, 'saveID') && compareObjects(currentParams.route, postParams.route, 'saveID')) {
      return true;
    }
  }
  return false;
};

export const findOptionText = (optionsList, value) => (optionsList?.find(c => c.value === value)?.text);

export const reportOptions = routeName => {
  if (routeName === 'funnelPosition') return REPORT_TYPE_OPTIONS_FUNNEL_POSITION;
  return [];
};

/**
 * Returns the corresponding text value from the mapping object based on the analysis and value parameters.
 *
 * @param {string} analysis - The type of analysis, which can be 'channel', 'platform', 'audience_targeting' or 'ad_type'.
 * @param {string} value - The value to be analyzed.
 * @returns {string} - The corresponding text value from the mapping object.
 *
 * @example
 * const analysis = 'channel';
 * const value = 'affiliate';
 * const result = formatRBClassificationValue(analysis, value);
 * console.log(result); // Output: 'Affiliate'
 */
export const formatRBClassificationValue = (analysis, value) => {
  if (analysis === 'channel') {
    return CHANNEL_MAPPING[value]?.text || value;
  }
  if (analysis === 'platform') {
    return PLATFORM_MAPPING[value]?.text || value;
  }
  if (analysis === 'ad_type') {
    return AD_TYPE_MAPPING[value]?.text || value;
  }
  if (analysis === 'audience_targeting') {
    return AUDIENCE_TARGETING_MAPPING[value]?.text || value;
  }
  return '';
};

/**
 * Returns the RB classification text based on the provided analysis and values.
 *
 * @param {string} analysis - The type of analysis.
 * @param {string} analysisValue - The value associated with the analysis.
 * @returns {string} - The RB classification text.
 *
 * @example
 * const analysis = 'channel';
 * const analysisValue = 'value1';
 *
 * const rbClassificationText = getRBClassificationText(analysis, analysisValue);
 * console.log(rbClassificationText);
 * // Output: "Channel - Display"
 */
export const getRBClassificationText = (analysis, analysisValue) => {
  const formattedAnalysis = DEFAULT_DERIVED_TIERS[analysis] ? `${DEFAULT_DERIVED_TIERS[analysis]} - ` : false;

  if (!formattedAnalysis) return '';

  const formattedValue = typeof analysisValue === 'string'
    ? analysisValue
    : analysisValue.map(value => (
      formatRBClassificationValue(analysis, value)
    )).join(', ');
  return `${formattedAnalysis}${formattedValue}`;
};

export const convertToMacro = x => {
  const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
  const isDate = dateRegex.test(x);
  if (isDate) {
    const today = moment.utc().utcOffset(-5);
    const date = moment.utc(x, 'YYYY-MM-DD');
    const numDaysAgo = today.diff(date, 'days');
    return `{{ ${numDaysAgo}d }}`;
  }
  return x;
};

export const formattedHash = hash => {
  if (hash[0] !== '#') return {};
  const hashSplit = hash.substr(1).split('&');
  const saveHash = hashSplit.reduce((acc, v) => {
    const [key, val] = v.split('=');
    if (key === 'saveID') {
      return acc;
    }
    return { ...acc, [key]: val };
  }, {});
  return saveHash;
};

export const formattedParams = params => {
  if (!params) return {};
  return Object.keys(params)
    .reduce((accu, key) => {
      const value = convertToMacro(params[key]);
      accu[key] = value;
      return accu;
    }, {});
};

export const buildReportFields = (report, segments) => {
  if (!report) return [];

  const {
    report_name,
    filter_id,
    customer_type,
    model_type,
    group_by_tiers,
    group_by_period,
    email_recipients,
    cadence,
    report_config_id,
    start_date,
    end_date,
    status,
  } = report;

  const conversionName = segments?.find(({ identifier }) => identifier === filter_id)?.action_name || filter_id;

  const fields = [
    { label: 'Report Name', info: report_name },
    { label: 'Status', info: status ? status.charAt(0).toUpperCase() + status.substring(1).toLowerCase() : 'Unknown' },
    { label: 'Date Range', info: `${start_date} - ${end_date}` },
    { label: 'Conversion', info: conversionName },
    { label: 'Customer Type', info: customer_type === 'new' ? 'New' : customer_type === 'repeat' ? 'Repeat' : 'All' },
    { label: 'Attribution Model', info: model_type === 'first' ? 'First Touch' : model_type === 'last' ? 'Last Touch' : model_type === 'even' ? 'Even Split' : 'Normalized' },
    { label: 'Group By Dimensions', info: group_by_tiers?.join(', ') },
    { label: 'Group By Time Period', info: group_by_period ? `${group_by_period.charAt(0).toUpperCase()}${group_by_period.slice(1)}` : '' },
    { label: 'Email Recipients', info: email_recipients?.join(', ') },
    { label: 'Metrics', info: METRICS.map(metric => metric).join(', ') },
  ];

  // Add report type field if report_config_id is defined
  if (typeof report_config_id !== 'undefined') {
    fields.push({ label: 'Report Type', info: report_config_id === 0 ? 'Adhoc' : 'Scheduled' });
  }

  // Add cadence field if it exists
  if (cadence) {
    const formatCadence = cadenceValue => {
      const cadenceMap = {
        day: 'Daily',
        week: 'Weekly',
        month: 'Monthly',
      };
      return cadenceMap[cadenceValue] || cadenceValue;
    };

    fields.push({
      label: 'Cadence',
      info: formatCadence(cadence),
    });
  }

  return fields;
};
